<template>
  <page-header-wrapper>
    <div class="business-page erp-list-page baidao-list-page expense-detail-page">
      <section class="erp-search-card">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="8" :sm="24">
              <a-form-item label="日期">
                <a-range-picker v-model="dateRange" style="width: 100%" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="单据编码">
                <a-input v-model="query.documentNo" allow-clear placeholder="费用单号" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="客商">
                <a-input v-model="query.customerKeyword" allow-clear placeholder="客商编号或名称" @pressEnter="search" />
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24" class="erp-search-actions">
              <a-button type="primary" icon="search" @click="search">查询</a-button>
              <a-button icon="redo" @click="resetQuery">重置</a-button>
            </a-col>
          </a-row>
        </a-form>
      </section>

      <section class="erp-table-card">
        <div class="erp-table-toolbar">
          <div>
            <h3>费用明细表</h3>
            <p>展示已审核、未作废的客户费用单（finance_document.document_type=expense）；费用总额取单头 total_amount。</p>
          </div>
          <div class="erp-toolbar-actions">
            <span v-if="pagination.total" class="expense-detail-totals">
              费用合计 <strong class="erp-money">¥{{ formatMoney(expenseTotalAmount) }}</strong>
            </span>
            <a-button
              v-hasPermi="['erp:report:expense-detail:export']"
              icon="download"
              :loading="exporting"
              :disabled="!pagination.total"
              @click="doExport">导出</a-button>
          </div>
        </div>
        <a-table
          row-key="id"
          bordered
          size="middle"
          :columns="tableColumns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          :scroll="tableScroll"
          :locale="{ emptyText: '暂无费用明细' }"
          @change="onPageChange">
          <template slot="text" slot-scope="value">
            <span :title="displayValue(value)">{{ displayValue(value) }}</span>
          </template>
          <template slot="money" slot-scope="value"><span class="erp-money">¥{{ formatMoney(value) }}</span></template>
        </a-table>
      </section>
    </div>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import {
  pageExpenseDetailReport,
  exportExpenseDetailReport
} from '@/api/erp/expenseDetailReport'

const emptyQuery = () => ({
  documentNo: '',
  customerKeyword: ''
})

export default {
  name: 'ErpExpenseDetailReport',
  data () {
    return {
      query: emptyQuery(),
      dateRange: [],
      rows: [],
      expenseTotalAmount: 0,
      loading: false,
      exporting: false,
      pagination: { current: 1, pageSize: 10, total: 0, showSizeChanger: true, showTotal: total => `共 ${total} 条` }
    }
  },
  computed: {
    tableScroll () {
      const w = this.tableColumns.reduce((sum, col) => sum + (col.width || 0), 0)
      return w > 0 ? { x: w } : undefined
    },
    tableColumns () {
      return [
        { title: '日期', dataIndex: 'expenseDate', width: 120, scopedSlots: { customRender: 'text' } },
        { title: '单据编码', dataIndex: 'documentNo', width: 200, scopedSlots: { customRender: 'text' } },
        { title: '客商编号', dataIndex: 'customerCode', width: 140, scopedSlots: { customRender: 'text' } },
        { title: '客商名称', dataIndex: 'customerName', width: 220, scopedSlots: { customRender: 'text' } },
        { title: '费用总额', dataIndex: 'expenseTotalAmount', width: 120, scopedSlots: { customRender: 'money' } }
      ]
    }
  },
  created () {
    this.load()
  },
  methods: {
    displayValue (value) {
      return value === null || value === undefined || value === '' ? '—' : value
    },
    formatMoney (value) {
      return Number(value || 0).toFixed(2)
    },
    queryParams () {
      return {
        documentNo: (this.query.documentNo || '').trim() || undefined,
        customerKeyword: (this.query.customerKeyword || '').trim() || undefined,
        dateStart: this.dateRange[0] ? this.dateRange[0].format('YYYY-MM-DD') : undefined,
        dateEnd: this.dateRange[1] ? this.dateRange[1].format('YYYY-MM-DD') : undefined,
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize
      }
    },
    load () {
      this.loading = true
      pageExpenseDetailReport(this.queryParams()).then(res => {
        const data = res.data || {}
        this.rows = data.records || []
        this.pagination.total = Number(data.total || 0)
        this.expenseTotalAmount = Number(data.expenseTotalAmount || 0)
      }).catch(() => {
        this.rows = []
        this.pagination.total = 0
        this.expenseTotalAmount = 0
      }).finally(() => { this.loading = false })
    },
    search () {
      this.pagination.current = 1
      this.load()
    },
    resetQuery () {
      this.query = emptyQuery()
      this.dateRange = []
      this.pagination.current = 1
      this.load()
    },
    onPageChange (pag) {
      this.pagination.current = pag.current
      this.pagination.pageSize = pag.pageSize
      this.load()
    },
    async doExport () {
      this.exporting = true
      try {
        const params = { ...this.queryParams() }
        delete params.pageNum
        delete params.pageSize
        const res = await exportExpenseDetailReport(params)
        const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `费用明细_${moment().format('YYYYMMDDHHmmss')}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.expense-detail-totals {
  margin-right: 12px;
  color: rgba(0, 0, 0, 0.65);
  font-size: 13px;
}
.expense-detail-totals strong {
  margin-left: 4px;
}
</style>
